import { Modal, Form, Input, Select } from 'antd';
import { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useMutation } from '@tanstack/react-query';
import Button from 'app/atoms/Button';
import { UserRole, UserRoleLabelMap } from 'enums/user';
import { inviteUser } from './service';
import { InviteUserPayload, User } from './types';

interface InviteUserModalProps {
  visible: boolean;
  onCancel: () => void;
  onInvited: (user: User) => void;
}

const roleOptions = Object.values(UserRole).map((role) => ({ label: UserRoleLabelMap[role], value: role }));

const InviteUserModal: React.FC<InviteUserModalProps> = ({ visible, onCancel, onInvited }) => {
  const [form] = Form.useForm<InviteUserPayload>();

  const inviteMutation = useMutation({
    mutationFn: inviteUser,
    onSuccess: (user) => {
      toast.success(`Invitation sent to ${user.email}`);
      form.resetFields();
      onInvited(user);
    },
    onError: (error: AxiosError<{ message?: string }>) => {
      toast.error(error.response?.data?.message ?? 'Failed to invite user');
    },
  });

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal title="Invite New User" open={visible} onCancel={handleCancel} footer={null} destroyOnClose>
      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => inviteMutation.mutate(values)}
        initialValues={{ role: UserRole.CONTENT_EDITOR }}
      >
        <Form.Item
          name="email"
          label="Email Address"
          rules={[{ required: true, type: 'email', message: 'Enter a valid email address' }]}
        >
          <Input placeholder="name@company.com" />
        </Form.Item>
        <Form.Item name="displayName" label="Display Name">
          <Input placeholder="Optional - falls back to their Microsoft profile name on first sign-in" />
        </Form.Item>
        <Form.Item name="role" label="Role" rules={[{ required: true }]}>
          <Select options={roleOptions} />
        </Form.Item>
        <div className="flex justify-end gap-3 mt-4">
          <Button variant="secondary" onClick={handleCancel}>
            Cancel
          </Button>
          <Button variant="primary" htmlType="submit" loading={inviteMutation.isPending}>
            Send Invite
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default InviteUserModal;
